import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { TopNav } from "@/components/layout/TopNav";
import { ErrorScreen } from "@/components/layout/ErrorScreen";
import { useTheme } from "@/themes/useTheme";

interface RouterContext {
  queryClient: QueryClient;
}

// The HTML shell lives in index.html now that this is a single-page app, so
// the root route owns only what is inside <body>.
export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: ({ error }) => <ErrorScreen error={error} />,
  notFoundComponent: () => (
    <ErrorScreen error={new Error("That page does not exist.")} />
  ),
});

/**
 * The application frame: a viewport-height column, nav on top, one route below.
 *
 * `h-dvh` rather than `h-screen` because on iOS Safari `100vh` is the height
 * the viewport has when the address bar is *hidden*, so a `h-screen` shell is
 * always a browser-chrome taller than the window and the bottom row of every
 * table sits under the toolbar.
 *
 * `overflow-hidden` is what makes the document itself unscrollable, so each
 * route can own its single scroll region (see components/layout/Page.tsx).
 * The backdrop gradient then covers exactly the viewport and stays put, which
 * is also what keeps the white page headings legible: they never travel down
 * into the pale end of it.
 */
function RootComponent() {
  useTheme();
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-backdrop">
      <TopNav />
      <main className="min-h-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
}
